<script setup lang="ts">
import { computed } from 'vue';

import GreatestHitsBadge from '@/components/base/GreatestHitsBadge.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Caption from '@/components/base/typography/Caption.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import PricePresentation from '@/components/pdp/buy-block/PricePresentation.vue';
import { ApprovedReviews } from '@/lib/reviews';
import { ImageBySize } from '@/utils/image';
import { NutsProduct, NutsProductVariant, PurchaseOptions } from '@/utils/product';

const props = defineProps<{
  image: ImageBySize;
  isGreatestHits: boolean;
  product: NutsProduct;
  reviews: ApprovedReviews;
  selectedVariant: NutsProductVariant;
  selections: PurchaseOptions;
}>();

defineEmits<{
  'show-full-pdp': [buttonName: string];
}>();

const showRating = computed(() => props.reviews.averageRating >= 3);
const formattedRating = computed(() => props.reviews.averageRating.toFixed(1));
</script>

<template>
  <div class="flex gap-4 items-center">
    <img :src="image.large" :alt="`${product.name} image`" class="object-cover w-32 h-full" />
    <div class="flex flex-col gap-2 items-start">
      <BaseBodyText class="font-bold">{{ product.name }}</BaseBodyText>
      <div class="flex gap-1 items-center">
        <PricePresentation
          :product
          :selectedVariant
          :selections
          :showDiscountBadge="false"
          smallText
        />
        <div v-if="showRating" class="flex items-center">
          <img
            aria-hidden="true"
            alt="star"
            class="object-contain mx-1 w-3 h-3 shrink-0"
            src="@/assets/pdp/star-full.svg"
          />
          <span class="sr-only" data-test="total-reviews">
            {{ formattedRating }} out of 5 stars
          </span>
          <Caption aria-hidden="true" class="text-neutral-500"> ({{ formattedRating }}) </Caption>
        </div>
        <div
          v-if="isGreatestHits"
          class="flex gap-1 items-center pl-1 border-l border-solid border-neutral-200"
        >
          <GreatestHitsBadge :size="16" />
          <SmallBodyText class="text-neutral-500">Greatest Hits</SmallBodyText>
        </div>
      </div>
      <UnstyledButton @click="$emit('show-full-pdp', 'See all sizes')">
        <SmallBodyText class="text-cyan-700 underline">See all sizes</SmallBodyText>
      </UnstyledButton>
    </div>
  </div>
</template>
